import { useCallback, useEffect, useRef, useState } from "react";
import { CheckCircle2, Eraser, Eye, Loader2, PenLine, Stamp } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { SignedImage } from "@/lib/signedFile";
import { appConfirm } from "@/lib/appDialog";

const DEFAULT_POSITION = { x: 0.43, y: 0.82, scale: 0.13 };

export default function CertificateApprovalEditor({ certificate, certificates, onApproved }) {
  const bulkCertificates = certificates?.length ? certificates : null;
  const bulk = Boolean(bulkCertificates);
  const suggestedName = certificate.official_name && certificate.official_name !== certificate.association_name
    ? certificate.official_name : "";
  const suggestedTitle = certificate.official_title === "المشرف الداخلي بالجمعية"
    ? "" : (certificate.official_title || "");
  const [officialName, setOfficialName] = useState(suggestedName);
  const [officialTitle, setOfficialTitle] = useState(suggestedTitle);
  const [signatureStrokes, setSignatureStrokes] = useState([]);
  const [sealPosition, setSealPosition] = useState(certificate.seal_position || DEFAULT_POSITION);
  const [busy, setBusy] = useState("");

  const payload = () => ({
    official_name: officialName.trim(),
    official_title: officialTitle.trim(),
    signature_strokes: signatureStrokes,
    seal_position: sealPosition,
    ...(bulk ? { certificate_ids: bulkCertificates.map((item) => item.id) } : {}),
  });

  const validate = () => {
    if (officialName.trim().length < 2) return "اكتب اسم المسؤول المعتمد";
    if (officialTitle.trim().length < 2) return "اكتب المسمى الوظيفي";
    const signaturePoints = signatureStrokes.flat();
    if (signaturePoints.length < 6) return "ارسم توقيع المسؤول بشكل واضح";
    const xs = signaturePoints.map((point) => point.x);
    const ys = signaturePoints.map((point) => point.y);
    if (Math.max(...xs) - Math.min(...xs) < 0.03 || Math.max(...ys) - Math.min(...ys) < 0.03) return "وسّع التوقيع داخل المساحة ليظهر بوضوح";
    if (!certificate.seal_file_id) return "ارفع ختم الجمعية أولًا";
    return "";
  };

  const preview = async () => {
    const error = validate();
    if (error) return toast.error(error);
    setBusy("preview");
    let objectUrl = "";
    try {
      const { data } = await api.post(
        bulk ? "/certificates/association/bulk-approval-preview" : `/certificates/${certificate.id}/approval-preview`,
        payload(),
        { responseType: "blob" },
      );
      objectUrl = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const popup = window.open(objectUrl, "_blank", "noopener,noreferrer");
      if (!popup) {
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = bulk ? "certificate-bulk-preview.pdf" : `certificate-${certificate.cert_id}-preview.pdf`;
        link.click();
      }
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    } catch (requestError) {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      toast.error(formatApiError(requestError));
    } finally {
      setBusy("");
    }
  };

  const approve = async () => {
    const error = validate();
    if (error) return toast.error(error);
    if (bulk && !await appConfirm(
      `سيتم اعتماد ${bulkCertificates.length} شهادة وإرسالها للطلاب، ولن تبقى ضمن المسودات. هل تريد المتابعة؟`,
      { title: "تأكيد الاعتماد الجماعي", confirmLabel: "اعتماد وإرسال" },
    )) return;
    setBusy("approve");
    try {
      await api.post(bulk ? "/certificates/association/bulk-approve" : `/certificates/${certificate.id}/approve`, payload());
      toast.success(bulk ? `تم اعتماد وإرسال ${bulkCertificates.length} شهادة` : "تم اعتماد الشهادة بالتوقيع والختم وإرسالها للطالب");
      if (typeof onApproved === "function") await onApproved();
    } catch (requestError) {
      toast.error(formatApiError(requestError));
    } finally {
      setBusy("");
    }
  };

  const preset = (x, y) => setSealPosition((current) => ({ ...current, x, y }));

  return <div className="mt-4 space-y-5 rounded-2xl border border-teal-200 bg-teal-50/40 p-4" data-testid={bulk ? "bulk-certificate-approval-editor" : `certificate-approval-editor-${certificate.id}`}>
    <div>
      <h4 className="font-heading text-lg font-black">{bulk ? `بيانات الاعتماد الجماعي (${bulkCertificates.length} شهادة)` : "بيانات اعتماد الشهادة"}</h4>
      <p className="mt-1 text-xs text-slate-600">{bulk ? "ستُطبق هذه البيانات والتوقيع والختم على جميع الشهادات المحددة. المعاينة تعرض أول شهادة كنموذج قبل الإرسال." : "لن تصدر الشهادة قبل اكتمال اسم المسؤول ومسمّاه وتوقيعه وموضع الختم."}</p>
    </div>

    <div className="grid gap-3 md:grid-cols-2">
      <label className="text-sm font-bold text-slate-700">اسم المسؤول المعتمد
        <input value={officialName} onChange={(event) => setOfficialName(event.target.value)} placeholder="مثال: أحمد محمد العتيبي" className="mt-1 min-h-11 w-full rounded-xl border bg-white px-3 font-normal" data-testid="certificate-official-name" />
      </label>
      <label className="text-sm font-bold text-slate-700">المسمى الوظيفي
        <input value={officialTitle} onChange={(event) => setOfficialTitle(event.target.value)} placeholder="مثال: مدير التدريب والتطوير" className="mt-1 min-h-11 w-full rounded-xl border bg-white px-3 font-normal" data-testid="certificate-official-title" />
      </label>
    </div>

    <SignaturePad onChange={setSignatureStrokes} />

    <section className="rounded-xl border bg-white p-4">
      <div className="flex items-center gap-2"><Stamp className="h-5 w-5 text-teal-700" /><h5 className="font-black">حدد موضع ظهور الختم</h5></div>
      <p className="mt-1 text-xs text-slate-500">اختر موضعًا سريعًا ثم عدّل الاتجاه والحجم إذا احتجت.</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" onClick={() => preset(0.35, 0.82)} className="rounded-lg border px-3 py-2 text-xs font-bold">يسار التوقيع</button>
        <button type="button" onClick={() => preset(0.50, 0.74)} className="rounded-lg border px-3 py-2 text-xs font-bold">فوق التوقيع</button>
        <button type="button" onClick={() => preset(0.65, 0.82)} className="rounded-lg border px-3 py-2 text-xs font-bold">يمين التوقيع</button>
      </div>
      <div className="mt-4 overflow-hidden rounded-xl border bg-gradient-to-b from-[#f8f3ea] to-white">
        <div className="relative aspect-[16/9]">
          <div className="absolute inset-x-[12%] top-[12%] h-2 rounded bg-slate-200" />
          <div className="absolute inset-x-[22%] top-[27%] h-2 rounded bg-slate-100" />
          <div className="absolute bottom-[10%] right-[10%] h-[18%] w-[28%] rounded border border-dashed border-slate-300" />
          <div className="absolute bottom-[10%] left-[35%] h-[18%] w-[25%] rounded border border-dashed border-teal-300" />
          <SignedImage
            fileId={certificate.seal_file_id}
            alt="موضع ختم الجمعية"
            className="absolute object-contain"
            style={{
              left: `${sealPosition.x * 100}%`,
              top: `${sealPosition.y * 100}%`,
              width: `${sealPosition.scale * 100}%`,
              height: `${sealPosition.scale * 68}%`,
              transform: "translate(-50%, -50%)",
            }}
          />
        </div>
      </div>
      <div className="mt-4 grid gap-3 md:grid-cols-3">
        <Range label="يمين / يسار" min={0.18} max={0.82} step={0.01} value={sealPosition.x} onChange={(x) => setSealPosition({ ...sealPosition, x })} />
        <Range label="أعلى / أسفل" min={0.62} max={0.90} step={0.01} value={sealPosition.y} onChange={(y) => setSealPosition({ ...sealPosition, y })} />
        <Range label="حجم الختم" min={0.08} max={0.22} step={0.01} value={sealPosition.scale} onChange={(scale) => setSealPosition({ ...sealPosition, scale })} />
      </div>
    </section>

    <div className="flex flex-wrap justify-end gap-2">
      <button type="button" onClick={preview} disabled={Boolean(busy)} className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-teal-700 bg-white px-4 text-sm font-bold text-teal-800 disabled:opacity-50" data-testid={bulk ? "bulk-certificate-approval-preview" : "certificate-approval-preview"}>
        {busy === "preview" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}{bulk ? "معاينة شهادة نموذجية" : "معاينة PDF بهذه الإعدادات"}
      </button>
      <button type="button" onClick={approve} disabled={Boolean(busy)} className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-teal-700 px-5 text-sm font-bold text-white disabled:opacity-50" data-testid={bulk ? "bulk-certificate-approval-submit" : "certificate-approval-submit"}>
        {busy === "approve" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}{bulk ? `اعتماد وإرسال ${bulkCertificates.length} شهادة` : "اعتماد وإرسال للطالب"}
      </button>
    </div>
  </div>;
}

function SignaturePad({ onChange }) {
  const canvasRef = useRef(null);
  const strokesRef = useRef([]);
  const currentRef = useRef(null);
  const drawingRef = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const ratio = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.floor(rect.width * ratio));
    canvas.height = Math.max(1, Math.floor(rect.height * ratio));
    const context = canvas.getContext("2d");
    context.scale(ratio, ratio);
    context.lineCap = "round"; context.lineJoin = "round"; context.lineWidth = 2.2; context.strokeStyle = "#0f172a";
    for (const stroke of strokesRef.current) {
      if (stroke.length < 2) continue;
      context.beginPath();
      stroke.forEach((point, index) => {
        const x = point.x * rect.width; const y = point.y * rect.height;
        if (index === 0) context.moveTo(x, y); else context.lineTo(x, y);
      });
      context.stroke();
    }
  }, []);

  useEffect(() => {
    redraw();
    window.addEventListener("resize", redraw);
    return () => window.removeEventListener("resize", redraw);
  }, [redraw]);

  const point = (event) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(1, Number(((event.clientX - rect.left) / rect.width).toFixed(5)))),
      y: Math.max(0, Math.min(1, Number(((event.clientY - rect.top) / rect.height).toFixed(5)))),
    };
  };
  const down = (event) => {
    event.preventDefault(); canvasRef.current?.setPointerCapture?.(event.pointerId);
    drawingRef.current = true; currentRef.current = [point(event)]; strokesRef.current.push(currentRef.current); redraw();
  };
  const move = (event) => {
    if (!drawingRef.current) return;
    event.preventDefault();
    const next = point(event); const stroke = currentRef.current; const last = stroke?.[stroke.length - 1];
    if (!stroke || (last && Math.hypot(next.x - last.x, next.y - last.y) < 0.003)) return;
    stroke.push(next); if (stroke.length > 2000) stroke.length = 2000; redraw();
  };
  const finish = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false; currentRef.current = null;
    strokesRef.current = strokesRef.current.filter((stroke) => stroke.length >= 2).slice(-100);
    setHasInk(strokesRef.current.length > 0); onChange(strokesRef.current.map((stroke) => [...stroke])); redraw();
  };
  const clear = () => {
    strokesRef.current = []; currentRef.current = null; drawingRef.current = false; setHasInk(false); onChange([]); redraw();
  };

  return <section className="rounded-xl border bg-white p-4">
    <div className="flex items-center justify-between gap-2"><div className="flex items-center gap-2"><PenLine className="h-5 w-5 text-teal-700" /><h5 className="font-black">توقيع المسؤول</h5></div><button type="button" onClick={clear} className="inline-flex items-center gap-1 rounded-lg border px-3 py-2 text-xs font-bold"><Eraser className="h-4 w-4" />مسح</button></div>
    <p className="mt-1 text-xs text-slate-500">ارسم التوقيع داخل المساحة؛ سيظهر الرسم نفسه على الشهادة.</p>
    <canvas ref={canvasRef} onPointerDown={down} onPointerMove={move} onPointerUp={finish} onPointerCancel={finish} onPointerLeave={finish} className="mt-3 h-36 w-full touch-none rounded-xl border-2 border-dashed border-slate-300 bg-slate-50" data-testid="certificate-signature-canvas" />
    <p className={`mt-2 text-xs font-bold ${hasInk ? "text-emerald-700" : "text-amber-700"}`}>{hasInk ? "تم التقاط التوقيع" : "التوقيع مطلوب قبل الاعتماد"}</p>
  </section>;
}

function Range({ label, value, onChange, ...props }) {
  return <label className="text-xs font-bold text-slate-600">{label}<input type="range" {...props} value={value} onChange={(event) => onChange(Number(event.target.value))} className="mt-2 w-full accent-teal-700" /></label>;
}
